import type { FormFieldOption } from './form-widget.types';

/**
 * Ubicaciones por país para los formularios: región (o estado/departamento) y
 * las ciudades de cada una. Ambos catálogos salen de la misma fuente, así que
 * los nombres de región siempre calzan entre los dos selectores.
 *
 * Se guarda el nombre legible, no un código interno, porque es lo que se
 * muestra en la tarjeta de la oferta. Sin comunas: el nivel de detalle es la
 * ciudad.
 */
const CITIES_BY_REGION: Record<string, Record<string, string[]>> = {
  ar: {
    'Ciudad Autónoma de Buenos Aires': ['Buenos Aires'],
    'Buenos Aires': [
      'La Plata',
      'Mar del Plata',
      'Bahía Blanca',
      'Quilmes',
      'Lomas de Zamora',
      'San Isidro',
      'Tigre',
      'Pilar',
      'Morón',
      'Tandil',
      'San Nicolás de los Arroyos',
      'Junín',
      'Olavarría',
    ],
    Catamarca: ['San Fernando del Valle de Catamarca'],
    Chaco: ['Resistencia', 'Presidencia Roque Sáenz Peña'],
    Chubut: ['Comodoro Rivadavia', 'Trelew', 'Puerto Madryn', 'Rawson', 'Esquel'],
    Córdoba: ['Córdoba', 'Río Cuarto', 'Villa María', 'Villa Carlos Paz', 'San Francisco', 'Alta Gracia'],
    Corrientes: ['Corrientes', 'Goya', 'Paso de los Libres'],
    'Entre Ríos': ['Paraná', 'Concordia', 'Gualeguaychú', 'Concepción del Uruguay'],
    Formosa: ['Formosa', 'Clorinda'],
    Jujuy: ['San Salvador de Jujuy', 'Palpalá', 'Libertador General San Martín'],
    'La Pampa': ['Santa Rosa', 'General Pico'],
    'La Rioja': ['La Rioja', 'Chilecito'],
    Mendoza: ['Mendoza', 'Godoy Cruz', 'Maipú', 'Luján de Cuyo', 'San Rafael'],
    Misiones: ['Posadas', 'Oberá', 'Eldorado', 'Puerto Iguazú'],
    Neuquén: ['Neuquén', 'Plottier', 'Cutral Có', 'San Martín de los Andes'],
    'Río Negro': ['San Carlos de Bariloche', 'General Roca', 'Cipolletti', 'Viedma'],
    Salta: ['Salta', 'San Ramón de la Nueva Orán', 'Tartagal'],
    'San Juan': ['San Juan', 'Chimbas', 'Caucete'],
    'San Luis': ['San Luis', 'Villa Mercedes', 'Merlo'],
    'Santa Cruz': ['Río Gallegos', 'Caleta Olivia', 'El Calafate'],
    'Santa Fe': ['Rosario', 'Santa Fe', 'Rafaela', 'Venado Tuerto', 'Villa Gobernador Gálvez'],
    'Santiago del Estero': ['Santiago del Estero', 'La Banda', 'Termas de Río Hondo'],
    'Tierra del Fuego': ['Ushuaia', 'Río Grande'],
    Tucumán: ['San Miguel de Tucumán', 'Yerba Buena', 'Tafí Viejo', 'Concepción'],
  },
  cl: {
    'Arica y Parinacota': ['Arica', 'Putre'],
    Tarapacá: ['Iquique', 'Alto Hospicio', 'Pozo Almonte'],
    Antofagasta: ['Antofagasta', 'Calama', 'Tocopilla', 'Mejillones', 'Taltal'],
    Atacama: ['Copiapó', 'Vallenar', 'Caldera', 'Chañaral'],
    Coquimbo: ['La Serena', 'Coquimbo', 'Ovalle', 'Illapel', 'Vicuña'],
    Valparaíso: [
      'Valparaíso',
      'Viña del Mar',
      'Quilpué',
      'Villa Alemana',
      'Concón',
      'Quillota',
      'San Antonio',
      'San Felipe',
      'Los Andes',
    ],
    'Metropolitana de Santiago': [
      'Santiago',
      'Puente Alto',
      'San Bernardo',
      'Colina',
      'Melipilla',
      'Talagante',
      'Buin',
      'Peñaflor',
    ],
    "Libertador General Bernardo O'Higgins": [
      'Rancagua',
      'Machalí',
      'San Fernando',
      'Rengo',
      'Santa Cruz',
      'Pichilemu',
    ],
    Maule: ['Talca', 'Curicó', 'Linares', 'Constitución', 'Molina', 'Cauquenes'],
    Ñuble: ['Chillán', 'San Carlos', 'Bulnes', 'Quirihue'],
    Biobío: [
      'Concepción',
      'Talcahuano',
      'San Pedro de la Paz',
      'Chiguayante',
      'Coronel',
      'Lota',
      'Los Ángeles',
    ],
    'La Araucanía': ['Temuco', 'Padre Las Casas', 'Villarrica', 'Pucón', 'Angol', 'Victoria', 'Lautaro'],
    'Los Ríos': ['Valdivia', 'La Unión', 'Panguipulli', 'Río Bueno'],
    'Los Lagos': ['Puerto Montt', 'Osorno', 'Puerto Varas', 'Castro', 'Ancud', 'Calbuco'],
    Aysén: ['Coyhaique', 'Puerto Aysén', 'Chile Chico'],
    'Magallanes y de la Antártica Chilena': ['Punta Arenas', 'Puerto Natales', 'Porvenir'],
  },
  mx: {
    Aguascalientes: ['Aguascalientes', 'Jesús María'],
    'Baja California': ['Tijuana', 'Mexicali', 'Ensenada', 'Rosarito', 'Tecate'],
    'Baja California Sur': ['La Paz', 'Cabo San Lucas', 'San José del Cabo', 'Ciudad Constitución'],
    Campeche: ['Campeche', 'Ciudad del Carmen'],
    Chiapas: ['Tuxtla Gutiérrez', 'San Cristóbal de las Casas', 'Tapachula', 'Comitán'],
    Chihuahua: ['Chihuahua', 'Ciudad Juárez', 'Delicias', 'Cuauhtémoc', 'Hidalgo del Parral'],
    'Ciudad de México': ['Ciudad de México'],
    Coahuila: ['Saltillo', 'Torreón', 'Monclova', 'Piedras Negras', 'Ciudad Acuña'],
    Colima: ['Colima', 'Villa de Álvarez', 'Manzanillo', 'Tecomán'],
    Durango: ['Durango', 'Gómez Palacio', 'Lerdo'],
    'Estado de México': [
      'Toluca',
      'Ecatepec',
      'Naucalpan de Juárez',
      'Nezahualcóyotl',
      'Tlalnepantla',
      'Cuautitlán Izcalli',
      'Atizapán de Zaragoza',
      'Metepec',
    ],
    Guanajuato: ['León', 'Irapuato', 'Celaya', 'Salamanca', 'Guanajuato', 'San Miguel de Allende'],
    Guerrero: ['Acapulco', 'Chilpancingo', 'Iguala', 'Zihuatanejo'],
    Hidalgo: ['Pachuca', 'Tulancingo', 'Tula de Allende', 'Tizayuca'],
    Jalisco: [
      'Guadalajara',
      'Zapopan',
      'Tlaquepaque',
      'Tonalá',
      'Tlajomulco de Zúñiga',
      'Puerto Vallarta',
      'Lagos de Moreno',
    ],
    Michoacán: ['Morelia', 'Uruapan', 'Zamora', 'Lázaro Cárdenas', 'Apatzingán'],
    Morelos: ['Cuernavaca', 'Jiutepec', 'Cuautla', 'Temixco'],
    Nayarit: ['Tepic', 'Bahía de Banderas', 'Xalisco'],
    'Nuevo León': [
      'Monterrey',
      'San Nicolás de los Garza',
      'Guadalupe',
      'Apodaca',
      'San Pedro Garza García',
      'Santa Catarina',
      'General Escobedo',
    ],
    Oaxaca: ['Oaxaca de Juárez', 'Salina Cruz', 'Tuxtepec', 'Huatulco'],
    Puebla: ['Puebla', 'Tehuacán', 'San Pedro Cholula', 'Atlixco', 'San Martín Texmelucan'],
    Querétaro: ['Querétaro', 'San Juan del Río', 'Corregidora', 'El Marqués'],
    'Quintana Roo': ['Cancún', 'Playa del Carmen', 'Chetumal', 'Cozumel', 'Tulum'],
    'San Luis Potosí': [
      'San Luis Potosí',
      'Soledad de Graciano Sánchez',
      'Ciudad Valles',
      'Matehuala',
    ],
    Sinaloa: ['Culiacán', 'Mazatlán', 'Los Mochis', 'Guasave'],
    Sonora: ['Hermosillo', 'Ciudad Obregón', 'Nogales', 'Navojoa', 'Guaymas', 'San Luis Río Colorado'],
    Tabasco: ['Villahermosa', 'Cárdenas', 'Comalcalco'],
    Tamaulipas: ['Reynosa', 'Matamoros', 'Nuevo Laredo', 'Tampico', 'Ciudad Victoria', 'Altamira'],
    Tlaxcala: ['Tlaxcala', 'Apizaco', 'Huamantla'],
    Veracruz: [
      'Veracruz',
      'Xalapa',
      'Boca del Río',
      'Coatzacoalcos',
      'Córdoba',
      'Orizaba',
      'Poza Rica',
    ],
    Yucatán: ['Mérida', 'Valladolid', 'Progreso', 'Tizimín'],
    Zacatecas: ['Zacatecas', 'Guadalupe', 'Fresnillo', 'Jerez'],
  },
  co: {
    Amazonas: ['Leticia'],
    Antioquia: ['Medellín', 'Bello', 'Itagüí', 'Envigado', 'Sabaneta', 'Rionegro', 'Apartadó', 'Turbo'],
    Arauca: ['Arauca', 'Saravena', 'Tame'],
    Atlántico: ['Barranquilla', 'Soledad', 'Malambo', 'Puerto Colombia'],
    'Bogotá D.C.': ['Bogotá'],
    Bolívar: ['Cartagena', 'Magangué', 'Turbaco'],
    Boyacá: ['Tunja', 'Duitama', 'Sogamoso', 'Chiquinquirá'],
    Caldas: ['Manizales', 'Villamaría', 'La Dorada', 'Chinchiná'],
    Caquetá: ['Florencia', 'San Vicente del Caguán'],
    Casanare: ['Yopal', 'Aguazul', 'Villanueva'],
    Cauca: ['Popayán', 'Santander de Quilichao', 'Puerto Tejada'],
    Cesar: ['Valledupar', 'Aguachica', 'Agustín Codazzi'],
    Chocó: ['Quibdó', 'Istmina'],
    Córdoba: ['Montería', 'Cereté', 'Lorica', 'Sahagún'],
    Cundinamarca: [
      'Soacha',
      'Zipaquirá',
      'Chía',
      'Facatativá',
      'Fusagasugá',
      'Girardot',
      'Mosquera',
      'Madrid',
      'Funza',
      'Cajicá',
    ],
    Guainía: ['Inírida'],
    Guaviare: ['San José del Guaviare'],
    Huila: ['Neiva', 'Pitalito', 'Garzón', 'La Plata'],
    'La Guajira': ['Riohacha', 'Maicao', 'Uribia'],
    Magdalena: ['Santa Marta', 'Ciénaga', 'Fundación'],
    Meta: ['Villavicencio', 'Acacías', 'Granada'],
    Nariño: ['Pasto', 'Ipiales', 'Tumaco', 'Túquerres'],
    'Norte de Santander': ['Cúcuta', 'Ocaña', 'Pamplona', 'Villa del Rosario'],
    Putumayo: ['Mocoa', 'Puerto Asís'],
    Quindío: ['Armenia', 'Calarcá', 'Montenegro', 'La Tebaida'],
    Risaralda: ['Pereira', 'Dosquebradas', 'Santa Rosa de Cabal', 'La Virginia'],
    'San Andrés y Providencia': ['San Andrés', 'Providencia'],
    Santander: [
      'Bucaramanga',
      'Floridablanca',
      'Girón',
      'Piedecuesta',
      'Barrancabermeja',
      'San Gil',
    ],
    Sucre: ['Sincelejo', 'Corozal', 'Santiago de Tolú'],
    Tolima: ['Ibagué', 'Espinal', 'Melgar', 'Honda', 'Chaparral'],
    'Valle del Cauca': ['Cali', 'Palmira', 'Buenaventura', 'Tuluá', 'Cartago', 'Buga', 'Jamundí', 'Yumbo'],
    Vaupés: ['Mitú'],
    Vichada: ['Puerto Carreño'],
  },
};

const toOptions = (names: string[]): FormFieldOption[] =>
  names.map((name) => ({ label: name, value: name }));

/** Clave = valor del campo de país (`cl` | `mx` | `co`). */
export const REGION_OPTIONS_BY_COUNTRY: Record<string, FormFieldOption[]> = Object.fromEntries(
  Object.entries(CITIES_BY_REGION).map(([country, regions]) => [
    country,
    toOptions(Object.keys(regions)),
  ]),
);

/** Clave = `${país}|${región}`, el orden en que las declara `optionsWhen.fields`. */
export const CITY_OPTIONS_BY_REGION: Record<string, FormFieldOption[]> = Object.fromEntries(
  Object.entries(CITIES_BY_REGION).flatMap(([country, regions]) =>
    Object.entries(regions).map(([region, cities]) => [`${country}|${region}`, toOptions(cities)]),
  ),
);

/**
 * Rangos de sueldo mensual bruto en moneda local. Se guardan como texto, así
 * que las ofertas quedan comparables entre sí en la tarjeta del listado.
 */
export const SALARY_OPTIONS_BY_COUNTRY: Record<string, FormFieldOption[]> = {
  ar: toOptions([
    'Menos de $500.000',
    '$500.000 – $800.000',
    '$800.000 – $1.200.000',
    '$1.200.000 – $1.800.000',
    '$1.800.000 – $2.500.000',
    'Más de $2.500.000',
    'A convenir',
  ]),
  cl: toOptions([
    'Menos de $700.000',
    '$700.000 – $1.000.000',
    '$1.000.000 – $1.500.000',
    '$1.500.000 – $2.000.000',
    '$2.000.000 – $3.000.000',
    'Más de $3.000.000',
    'A convenir',
  ]),
  mx: toOptions([
    'Menos de $10,000',
    '$10,000 – $15,000',
    '$15,000 – $25,000',
    '$25,000 – $40,000',
    '$40,000 – $60,000',
    'Más de $60,000',
    'A convenir',
  ]),
  co: toOptions([
    'Menos de $2.000.000',
    '$2.000.000 – $3.000.000',
    '$3.000.000 – $4.500.000',
    '$4.500.000 – $6.000.000',
    '$6.000.000 – $9.000.000',
    'Más de $9.000.000',
    'A convenir',
  ]),
};
